import { AddOn } from '../../../model';
import { PayOpenPortalPayable, PayWithoutInvoicePayable } from '../models';
import { ArTranAllocation, PaymentActionRequirement, PaymentPlatform } from '@aiwyn/api';
import { PaymentCancellationSource, PaymentIntentTrigger, PaymentSurface } from '../tracking';

export class MakePayment {
  static readonly type: string = '[PaymentsState] MakePayment';
}

export class SubmitCreditAllocations {
  static readonly type: string = '[PaymentsState] SubmitCreditAllocations';
  constructor(public isAlsoPayingAddOns: boolean = false) {}
}

export class ClearSuccessfulPaymentData {
  static readonly type: string = '[PaymentsState] ClearSuccessfulPaymentData';
}

export class SetCurrentPaymentActionRequirement {
  static readonly type: string = '[PaymentsState] SetCurrentPaymentActionRequirement';

  constructor(public paymentActionRequirement: PaymentActionRequirement) {}
}

export class ClearCurrentPaymentActionRequirement {
  static readonly type: string = '[PaymentsState] ClearCurrentPaymentActionRequirement';
}

export class CancelCurrentPayment {
  static readonly type: string = '[PaymentsState] CancelCurrentPayment';

  constructor(
    public cancellationReason: Error, // | string | LoadableError
    public cancellationSource: PaymentCancellationSource = 'unknown',
  ) {}
}

export class SetDefaultActivePlatform {
  static readonly type: string = '[PaymentsState] GetActivePlatform';
}

export class GetPayables {
  static readonly type: string = '[PaymentsState] GetPayables';
  constructor(public forceRefresh = false) {}
}

export class GetCreditLabels {
  static readonly type: string = '[PaymentsState] GetCreditLabels';
  constructor(public forceRefresh = false) {}
}

export class GetCredits {
  static readonly type: string = '[PaymentsState] GetCredits';
  constructor(public forceRefresh = false) {}
}

export class GetAddOnOptions {
  static readonly type: string = '[PaymentsState] GetAddOnOptions';
}

export class SetPayWithInvoices {
  static readonly type: string = '[PaymentsState] SetPayWithInvoices';
  constructor(public payWithInvoices: boolean) {}
}

export class UpdatePayWithoutInvoicePayable {
  static readonly type: string = '[PaymentsState] UpdatePayWithoutInvoicePayable';
  constructor(public partialPayWithoutInvoicePayable: Partial<PayWithoutInvoicePayable | PayOpenPortalPayable>) {}
}

export class GetLateFees {
  static readonly type: string = '[PaymentsState] GetLateFees';
  constructor(public forceRefresh = false) {}
}

export class ClearLateFees {
  static readonly type: string = '[PaymentsState] ClearLateFees';
}

export class SetPayLateFees {
  static readonly type: string = '[PaymentsState] SetPayLateFees';
  constructor(public payLateFees: boolean) {}
}

export class AddPayLateFeeClientIds {
  static readonly type: string = '[PaymentsState] AddPayLateFeeClientIds';
  constructor(public clientIds: number | number[]) {}
}

export class RemovePayLateFeeClientIds {
  static readonly type: string = '[PaymentsState] RemovePayLateFeeClientIds';
  constructor(public clientIds: number | number[]) {}
}

export class RemoveInapplicablePayLateFeeClientIds {
  static readonly type: string = '[PaymentsState] RemoveInapplicablePayLateFeeClientIds';
}

export class ClearPayLateFeeClientIds {
  static readonly type: string = '[PaymentsState] ClearPayLateFeeClientIds';
}

export class GetPaymentFeeConfigs {
  static readonly type: string = '[PaymentsState] GetPaymentFeeConfigs';
  constructor(public forceRefresh = false) {}
}

export class SetShouldSaveNewPaymentMethod {
  static readonly type: string = '[PaymentsState] SetShouldSavePaymentMethod';
  constructor(public shouldSaveNewPaymentMethod: boolean) {}
}

export class SetShouldSetNewPaymentMethodAsDefault {
  static readonly type: string = '[PaymentsState] SetShouldSetNewPaymentMethodAsDefault';
  constructor(public shouldSetNewPaymentMethodAsDefault: boolean) {}
}

export class ClearNewPaymentMethodData {
  static readonly type: string = '[PaymentsState] ClearNewPaymentMethodData';
}

export class ApplyPaymentToArTranAllocations {
  static readonly type: string = '[PaymentsState] ApplyPaymentToArTranAllocations';
}

export class SetArTranAllocations {
  static readonly type: string = '[PaymentsState] SetArTranAllocations';
  constructor(public arTranAllocations: ArTranAllocation | ArTranAllocation[]) {}
}

export class UpdateArTranAllocations {
  static readonly type: string = '[PaymentsState] UpdateArTranAllocations';
  constructor(
    public arTranAllocations: ArTranAllocation | ArTranAllocation[],
    // PP-3775: defaults to 'user' so manual selections (selectRow) are tagged as intent without any
    // change at the call site. Auto-selection paths (review-and-pay URL rehydration) pass 'auto'
    // explicitly so the funnel can exclude them from the "started" step.
    public intentTrigger: PaymentIntentTrigger = 'user',
  ) {}
}

export class RemoveArTranAllocations {
  static readonly type: string = '[PaymentsState] RemoveArTranAllocations';
  constructor(public arTranIds: number | number[]) {}
}

export class ClearArTranAllocations {
  static readonly type: string = '[PaymentsState] ClearArTranAllocations';
}

export class FillPartialArTranAllocations {
  static readonly type: string = '[PaymentsState] FillPartialArTranAllocations';
}

export class AddCreditAppliedClientIds {
  static readonly type: string = '[PaymentsState] AddCreditAppliedClientIds';
  constructor(public clientIds: number | number[]) {}
}

export class RemoveCreditAppliedClientIds {
  static readonly type: string = '[PaymentsState] RemoveCreditAppliedClientIds';
  constructor(public clientIds: number | number[]) {}
}

export class RemoveInapplicableCreditAppliedClientIds {
  static readonly type: string = '[PaymentsState] RemoveInapplicableCreditAppliedClientIds';
}

export class ClearCreditAppliedClientIds {
  static readonly type: string = '[PaymentsState] ClearCreditAppliedClientIds';
}

export class AddAddOn {
  static readonly type: string = '[PaymentsState] AddAddOn';
  constructor(public addOn: AddOn) {}
}

export class RemoveAddOn {
  static readonly type: string = '[PaymentsState] RemoveAddOn';
  constructor(public addOnId: number) {}
}

export class SetAddOns {
  static readonly type: string = '[PaymentsState] SetAddOns';
  constructor(public addOns: AddOn[]) {}
}

export class ClearAddOns {
  static readonly type: string = '[PaymentsState] ClearAddOns';
}

export class RemovePayWithoutInvoicePayable {
  static readonly type: string = '[PaymentsState] RemovePayWithoutInvoicePayable';
}

export class SetActivePlatform {
  static readonly type: string = '[PaymentsState] SetActivePlatform';
  constructor(public activePlatform: PaymentPlatform) {}
}

export class GetJobs {
  static readonly type: string = '[PaymentsState] GetJobs';
  constructor(public forceRefresh = false) {}
}

export class GetClients {
  static readonly type: string = '[PaymentsState] GetClients';
  constructor(public forceRefresh = false) {}
}

export class SetProvidedPrimaryRecipientEmail {
  static readonly type: string = '[PaymentsState] SetProvidedPrimaryRecipientEmail';
  constructor(public providedPrimaryRecipientEmail: string) {}
}

export class SetAdditionalRecipientEmail {
  static readonly type: string = '[PaymentsState] SetAdditionalRecipientEmail';
  constructor(public additionalRecipientEmails: string[]) {}
}

export class SetCustomProcessingFeeRate {
  static readonly type: string = '[PaymentsState] SetCustomProcessingFee';
  constructor(public customProcessingFeeRate: number) {}
}

export class GetQuickPaymentLinkId {
  static readonly type = '[PaymentsState] GetQuickPaymentLinkId';
  constructor(public quickPaymentLink: string) {}
}

export class ClearQuickPaymentLinkId {
  static readonly type = '[PaymentsState] ClearQuickPaymentLinkId';
}

/**
 * PP-3775: pin the entry source for the payment flow when the URL alone can't disambiguate it. The
 * pay-invoice email link and the in-portal "Review Invoices" button both land on `/reviewAndPay`. The
 * email origin (and, per ART-8293, whether it was a new-invoice notification vs. a collections/dunning
 * statement email — `pay-invoice-email-invoice` vs. `pay-invoice-email-collections`) is carried as
 * navigation `state` by `PayInvoiceAuthGuard` and translated into this action by `ReviewAndPayComponent`
 * on load; `PaymentsState.resolvePaymentSurface()` then prefers it on the review-and-pay branch. Pass
 * `undefined` to clear back to pure URL classification.
 */
export class SetPaymentEntrySource {
  static readonly type: string = '[PaymentsState] SetPaymentEntrySource';
  constructor(public paymentEntrySource: PaymentSurface | undefined) {}
}
